// =========================================================================
// 🎨 GRAPHIC BORDERS & FRAMES FOR PRODUCT IMAGES
// Provides 9-slice SVG graphic borders, presets, and custom border-image tools
// =========================================================================

import { addUnit, resolveMediaUrl } from '../utils/styleHelpers.js';

// SVG to base64 Data URI helper
const svgToDataUri = (svgString) => {
    if (typeof window !== 'undefined' && window.btoa) {
        return `data:image/svg+xml;base64,${window.btoa(unescape(encodeURIComponent(svgString)))}`;
    }
    // Node.js fallback for tests/SSR
    return `data:image/svg+xml;base64,${Buffer.from(svgString, 'utf-8').toString('base64')}`;
};

// --- PRESET SVG GENERATORS (9-Slice Geometry: 90x90, slice: 30) ---

// 1. Copper Island Metallic (Dual-line copper & bronze with corner diamonds)
const getCopperIslandSvg = (tint) => {
    const c1 = tint || '#d97736';
    const c2 = '#f59e0b';
    const c3 = '#78350f';
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="90" height="90" viewBox="0 0 90 90">
  <defs>
    <linearGradient id="cop1" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="${c2}" />
      <stop offset="50%" stop-color="${c1}" />
      <stop offset="100%" stop-color="${c3}" />
    </linearGradient>
    <linearGradient id="cop2" x1="100%" y1="0%" x2="0%" y2="100%">
      <stop offset="0%" stop-color="${c3}" />
      <stop offset="50%" stop-color="${c2}" />
      <stop offset="100%" stop-color="${c1}" />
    </linearGradient>
  </defs>
  <!-- Outer metallic border -->
  <rect x="4" y="4" width="82" height="82" fill="none" stroke="url(#cop1)" stroke-width="4" />
  <!-- Secondary inner frame -->
  <rect x="14" y="14" width="62" height="62" fill="none" stroke="url(#cop2)" stroke-width="2" />
  <!-- Corner notched accent diamonds -->
  <polygon points="14,6 22,14 14,22 6,14" fill="${c2}" stroke="${c3}" stroke-width="1.5" />
  <polygon points="76,6 84,14 76,22 68,14" fill="${c2}" stroke="${c3}" stroke-width="1.5" />
  <polygon points="14,68 22,76 14,84 6,76" fill="${c2}" stroke="${c3}" stroke-width="1.5" />
  <polygon points="76,68 84,76 76,84 68,76" fill="${c2}" stroke="${c3}" stroke-width="1.5" />
  <!-- Corner center rivets -->
  <circle cx="14" cy="14" r="2.5" fill="#ffffff" />
  <circle cx="76" cy="14" r="2.5" fill="#ffffff" />
  <circle cx="14" cy="76" r="2.5" fill="#ffffff" />
  <circle cx="76" cy="76" r="2.5" fill="#ffffff" />
  <rect x="30" y="30" width="30" height="30" fill="none" />
</svg>`;
    return svgToDataUri(svg);
};

// 2. Distillery Rustic Woodcut (Engraved antique distillery border with rosettes)
const getDistilleryRusticSvg = (tint) => {
    const c = tint || '#292524';
    const acc = '#a8a29e';
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="90" height="90" viewBox="0 0 90 90">
  <!-- Thick outer rule -->
  <rect x="4" y="4" width="82" height="82" fill="none" stroke="${c}" stroke-width="5" />
  <!-- Thin inner rule -->
  <rect x="13" y="13" width="64" height="64" fill="none" stroke="${c}" stroke-width="1.5" />
  <!-- Vintage hatch marks in edges -->
  <line x1="45" y1="4" x2="45" y2="13" stroke="${c}" stroke-width="2" />
  <line x1="4" y1="45" x2="13" y2="45" stroke="${c}" stroke-width="2" />
  <line x1="86" y1="45" x2="77" y2="45" stroke="${c}" stroke-width="2" />
  <line x1="45" y1="86" x2="45" y2="77" stroke="${c}" stroke-width="2" />
  <!-- Corner square blocks -->
  <rect x="7" y="7" width="14" height="14" fill="${c}" />
  <rect x="69" y="7" width="14" height="14" fill="${c}" />
  <rect x="7" y="69" width="14" height="14" fill="${c}" />
  <rect x="69" y="69" width="14" height="14" fill="${c}" />
  <!-- Corner antique florets -->
  <circle cx="14" cy="14" r="3" fill="#ffffff" />
  <circle cx="76" cy="14" r="3" fill="#ffffff" />
  <circle cx="14" cy="76" r="3" fill="#ffffff" />
  <circle cx="76" cy="76" r="3" fill="#ffffff" />
  <rect x="30" y="30" width="30" height="30" fill="none" />
</svg>`;
    return svgToDataUri(svg);
};

// 3. Vintage Filigree (Victorian flourish corner scrollwork)
const getVintageFiligreeSvg = (tint) => {
    const c = tint || '#475569';
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="90" height="90" viewBox="0 0 90 90">
  <!-- Dual frame line -->
  <rect x="5" y="5" width="80" height="80" fill="none" stroke="${c}" stroke-width="2" />
  <rect x="11" y="11" width="68" height="68" fill="none" stroke="${c}" stroke-width="1" stroke-dasharray="3,2" />
  <!-- Top-left flourish -->
  <path d="M 5,26 C 14,24 24,14 26,5 M 10,22 C 16,18 18,16 22,10 M 7,7 Q 16,16 19,19" fill="none" stroke="${c}" stroke-width="2.5" stroke-linecap="round" />
  <!-- Top-right flourish -->
  <path d="M 85,26 C 76,24 66,14 64,5 M 80,22 C 74,18 72,16 68,10 M 83,7 Q 74,16 71,19" fill="none" stroke="${c}" stroke-width="2.5" stroke-linecap="round" />
  <!-- Bottom-left flourish -->
  <path d="M 5,64 C 14,66 24,76 26,85 M 10,68 C 16,72 18,74 22,80 M 7,83 Q 16,74 19,71" fill="none" stroke="${c}" stroke-width="2.5" stroke-linecap="round" />
  <!-- Bottom-right flourish -->
  <path d="M 85,64 C 76,66 66,76 64,85 M 80,68 C 74,72 72,74 68,80 M 83,83 Q 74,74 71,71" fill="none" stroke="${c}" stroke-width="2.5" stroke-linecap="round" />
  <rect x="30" y="30" width="30" height="30" fill="none" />
</svg>`;
    return svgToDataUri(svg);
};

// 4. Artisan Spirits Bottle Label (Notched cut-in corners)
const getNotchedLabelSvg = (tint) => {
    const c = tint || '#1e293b';
    const inner = tint ? '#64748b' : '#94a3b8';
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="90" height="90" viewBox="0 0 90 90">
  <!-- Outer notched border path -->
  <path d="M 18,4 L 72,4 A 14,14 0 0,0 86,18 L 86,72 A 14,14 0 0,0 72,86 L 18,86 A 14,14 0 0,0 4,72 L 4,18 A 14,14 0 0,0 18,4 Z" fill="none" stroke="${c}" stroke-width="3.5" />
  <!-- Inner parallel hairline -->
  <path d="M 20,10 L 70,10 A 10,10 0 0,0 80,20 L 80,70 A 10,10 0 0,0 70,80 L 20,80 A 10,10 0 0,0 10,70 L 10,20 A 10,10 0 0,0 20,10 Z" fill="none" stroke="${inner}" stroke-width="1.5" stroke-dasharray="4,2" />
  <!-- Corner pips -->
  <circle cx="14" cy="14" r="2.5" fill="${c}" />
  <circle cx="76" cy="14" r="2.5" fill="${c}" />
  <circle cx="14" cy="76" r="2.5" fill="${c}" />
  <circle cx="76" cy="76" r="2.5" fill="${c}" />
  <rect x="30" y="30" width="30" height="30" fill="none" />
</svg>`;
    return svgToDataUri(svg);
};

// 5. Antique Gold Leaf (Regal Baroque Gold)
const getAntiqueGoldSvg = (tint) => {
    const g1 = tint || '#ca8a04';
    const g2 = '#fef08a';
    const g3 = '#854d0e';
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="90" height="90" viewBox="0 0 90 90">
  <defs>
    <linearGradient id="goldGrad" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="${g2}" />
      <stop offset="40%" stop-color="${g1}" />
      <stop offset="80%" stop-color="${g3}" />
      <stop offset="100%" stop-color="${g2}" />
    </linearGradient>
  </defs>
  <!-- Outer Gold Frame -->
  <rect x="4" y="4" width="82" height="82" fill="none" stroke="url(#goldGrad)" stroke-width="4.5" />
  <!-- Inner Fine Line -->
  <rect x="13" y="13" width="64" height="64" fill="none" stroke="url(#goldGrad)" stroke-width="1.5" />
  <!-- Baroque Corner Medallions -->
  <path d="M 6,18 Q 18,18 18,6 L 24,6 Q 24,24 6,24 Z" fill="url(#goldGrad)" />
  <path d="M 84,18 Q 72,18 72,6 L 66,6 Q 66,24 84,24 Z" fill="url(#goldGrad)" />
  <path d="M 6,72 Q 18,72 18,84 L 24,84 Q 24,66 6,66 Z" fill="url(#goldGrad)" />
  <path d="M 84,72 Q 72,72 72,84 L 66,84 Q 66,66 84,66 Z" fill="url(#goldGrad)" />
  <circle cx="15" cy="15" r="3" fill="${g2}" />
  <circle cx="75" cy="15" r="3" fill="${g2}" />
  <circle cx="15" cy="75" r="3" fill="${g2}" />
  <circle cx="75" cy="75" r="3" fill="${g2}" />
  <rect x="30" y="30" width="30" height="30" fill="none" />
</svg>`;
    return svgToDataUri(svg);
};

// 6. Postage Stamp Perforated Edge
const getPostageStampSvg = (tint) => {
    const c = tint || '#cbd5e1';
    const dark = tint ? '#334155' : '#64748b';
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="90" height="90" viewBox="0 0 90 90">
  <!-- Solid frame background with scalloped perforation dots -->
  <rect x="6" y="6" width="78" height="78" fill="none" stroke="${dark}" stroke-width="2" />
  <rect x="12" y="12" width="66" height="66" fill="none" stroke="${c}" stroke-width="1" />
  <!-- Scalloped holes on edges -->
  <circle cx="15" cy="4" r="3" fill="${dark}" />
  <circle cx="30" cy="4" r="3" fill="${dark}" />
  <circle cx="45" cy="4" r="3" fill="${dark}" />
  <circle cx="60" cy="4" r="3" fill="${dark}" />
  <circle cx="75" cy="4" r="3" fill="${dark}" />
  
  <circle cx="15" cy="86" r="3" fill="${dark}" />
  <circle cx="30" cy="86" r="3" fill="${dark}" />
  <circle cx="45" cy="86" r="3" fill="${dark}" />
  <circle cx="60" cy="86" r="3" fill="${dark}" />
  <circle cx="75" cy="86" r="3" fill="${dark}" />
  
  <circle cx="4" cy="15" r="3" fill="${dark}" />
  <circle cx="4" cy="30" r="3" fill="${dark}" />
  <circle cx="4" cy="45" r="3" fill="${dark}" />
  <circle cx="4" cy="60" r="3" fill="${dark}" />
  <circle cx="4" cy="75" r="3" fill="${dark}" />
  
  <circle cx="86" cy="15" r="3" fill="${dark}" />
  <circle cx="86" cy="30" r="3" fill="${dark}" />
  <circle cx="86" cy="45" r="3" fill="${dark}" />
  <circle cx="86" cy="60" r="3" fill="${dark}" />
  <circle cx="86" cy="75" r="3" fill="${dark}" />
  <rect x="30" y="30" width="30" height="30" fill="none" />
</svg>`;
    return svgToDataUri(svg);
};

// 7. Modern Precision Brackets (Minimalist architectural framing)
const getModernBracketsSvg = (tint) => {
    const c = tint || '#2563eb';
    const sub = tint ? '#93c5fd' : '#cbd5e1';
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="90" height="90" viewBox="0 0 90 90">
  <!-- Subtle continuous hairline -->
  <rect x="10" y="10" width="70" height="70" fill="none" stroke="${sub}" stroke-width="1" stroke-opacity="0.6" />
  <!-- Heavy Corner L-Brackets -->
  <path d="M 6,24 L 6,6 L 24,6" fill="none" stroke="${c}" stroke-width="4" stroke-linecap="square" />
  <path d="M 84,24 L 84,6 L 66,6" fill="none" stroke="${c}" stroke-width="4" stroke-linecap="square" />
  <path d="M 6,66 L 6,84 L 24,84" fill="none" stroke="${c}" stroke-width="4" stroke-linecap="square" />
  <path d="M 84,66 L 84,84 L 66,84" fill="none" stroke="${c}" stroke-width="4" stroke-linecap="square" />
  <!-- Precision corner dots -->
  <circle cx="12" cy="12" r="2" fill="${c}" />
  <circle cx="78" cy="12" r="2" fill="${c}" />
  <circle cx="12" cy="78" r="2" fill="${c}" />
  <circle cx="78" cy="78" r="2" fill="${c}" />
  <rect x="30" y="30" width="30" height="30" fill="none" />
</svg>`;
    return svgToDataUri(svg);
};

// 8. Classic Double Rule (Elegantly spaced double lines with corner squares)
const getDoubleRuleSvg = (tint) => {
    const c = tint || '#0f172a';
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="90" height="90" viewBox="0 0 90 90">
  <rect x="5" y="5" width="80" height="80" fill="none" stroke="${c}" stroke-width="3" />
  <rect x="13" y="13" width="64" height="64" fill="none" stroke="${c}" stroke-width="1.5" />
  <!-- Corner squares -->
  <rect x="7" y="7" width="4" height="4" fill="${c}" />
  <rect x="79" y="7" width="4" height="4" fill="${c}" />
  <rect x="7" y="79" width="4" height="4" fill="${c}" />
  <rect x="79" y="79" width="4" height="4" fill="${c}" />
  <rect x="30" y="30" width="30" height="30" fill="none" />
</svg>`;
    return svgToDataUri(svg);
};

// --- PRESETS CATALOG ---
export const GRAPHIC_BORDER_PRESETS = [
    {
        id: 'none',
        name: 'None (Standard Border)',
        desc: 'Standard clean CSS border or no frame',
        defaultWidth: '0px',
        slice: '0',
        repeat: 'stretch',
        defaultPadding: '0px'
    },
    {
        id: 'copper-island',
        name: 'Copper Island (Metallic)',
        desc: 'Dual metallic copper & bronze frame with corner diamonds — perfect for spirits bottles',
        defaultWidth: '18px',
        slice: '30',
        repeat: 'round',
        defaultPadding: '12px',
        defaultColor: '#d97736',
        getSvgDataUri: getCopperIslandSvg
    },
    {
        id: 'distillery-woodcut',
        name: 'Distillery Rustic (Woodcut)',
        desc: 'Handcrafted antique engraved border with corner rosettes & hatch marks',
        defaultWidth: '18px',
        slice: '30',
        repeat: 'round',
        defaultPadding: '12px',
        defaultColor: '#292524',
        getSvgDataUri: getDistilleryRusticSvg
    },
    {
        id: 'vintage-filigree',
        name: 'Vintage Victorian (Filigree)',
        desc: 'Intricate ornate scrollwork flourishes on all four corners',
        defaultWidth: '20px',
        slice: '30',
        repeat: 'round',
        defaultPadding: '14px',
        defaultColor: '#475569',
        getSvgDataUri: getVintageFiligreeSvg
    },
    {
        id: 'notched-label',
        name: 'Artisan Label (Notched Corners)',
        desc: 'Vintage spirits bottle label silhouette with cut-in circular corners',
        defaultWidth: '16px',
        slice: '30',
        repeat: 'round',
        defaultPadding: '10px',
        defaultColor: '#1e293b',
        getSvgDataUri: getNotchedLabelSvg
    },
    {
        id: 'antique-gold',
        name: 'Baroque Gold Leaf (Regal)',
        desc: 'Opulent dual-line gold frame with sculpted baroque corner medallions',
        defaultWidth: '20px',
        slice: '30',
        repeat: 'round',
        defaultPadding: '14px',
        defaultColor: '#ca8a04',
        getSvgDataUri: getAntiqueGoldSvg
    },
    {
        id: 'postage-stamp',
        name: 'Postage Stamp (Perforated)',
        desc: 'Classic perforated scalloped edge frame',
        defaultWidth: '16px',
        slice: '30',
        repeat: 'round',
        defaultPadding: '10px',
        defaultColor: '#64748b',
        getSvgDataUri: getPostageStampSvg
    },
    {
        id: 'minimal-brackets',
        name: 'Modern Precision Brackets',
        desc: 'Architectural precision corner L-brackets with clean hairlines',
        defaultWidth: '16px',
        slice: '30',
        repeat: 'stretch',
        defaultPadding: '8px',
        defaultColor: '#2563eb',
        getSvgDataUri: getModernBracketsSvg
    },
    {
        id: 'double-frame',
        name: 'Classic Double Rule',
        desc: 'Crisp dual concentric frame lines with corner accent pips',
        defaultWidth: '14px',
        slice: '30',
        repeat: 'round',
        defaultPadding: '8px',
        defaultColor: '#0f172a',
        getSvgDataUri: getDoubleRuleSvg
    },
    {
        id: 'custom',
        name: 'Custom Graphic Border (Upload / URL)',
        desc: 'Use your own custom PNG, SVG, or image border',
        defaultWidth: '16px',
        slice: '30',
        repeat: 'round',
        defaultPadding: '10px',
        defaultColor: ''
    }
];

/**
 * Returns the CSS style object for applying a graphic border to an image container.
 * @param {Object} styleSettings - Active customizer settings object
 * @returns {Object|null} CSS style object, or null if no graphic border is active
 */
export function getGraphicBorderStyle(styleSettings = {}) {
    const presetId = styleSettings.shopImageGraphicBorder || styleSettings.productImageGraphicBorder || 'none';
    if (!presetId || presetId === 'none') return null;

    const preset = GRAPHIC_BORDER_PRESETS.find(p => p.id === presetId);
    const customUrl = styleSettings.shopImageGraphicBorderUrl || styleSettings.productImageGraphicBorderUrl;
    const width = styleSettings.shopImageGraphicBorderWidth || styleSettings.productImageGraphicBorderWidth || preset?.defaultWidth || '16px';
    const slice = styleSettings.shopImageGraphicBorderSlice || styleSettings.productImageGraphicBorderSlice || preset?.slice || '30';
    const repeat = styleSettings.shopImageGraphicBorderRepeat || styleSettings.productImageGraphicBorderRepeat || preset?.repeat || 'round';
    const outset = styleSettings.shopImageGraphicBorderOutset || styleSettings.productImageGraphicBorderOutset || '0px';
    const tintColor = styleSettings.shopImageGraphicBorderColor || styleSettings.productImageGraphicBorderColor || '';

    let sourceUrl = '';
    if (presetId === 'custom' && customUrl) {
        sourceUrl = resolveMediaUrl(customUrl);
    } else if (preset && typeof preset.getSvgDataUri === 'function') {
        sourceUrl = preset.getSvgDataUri(tintColor);
    } else if (customUrl) {
        sourceUrl = resolveMediaUrl(customUrl);
    }

    if (!sourceUrl) return null;

    const parsedWidth = addUnit(width);

    return {
        borderStyle: 'solid',
        borderWidth: parsedWidth,
        borderColor: 'transparent',
        borderImageSource: `url("${sourceUrl}")`,
        borderImageSlice: slice,
        borderImageWidth: parsedWidth,
        borderImageRepeat: repeat,
        borderImageOutset: addUnit(outset),
        boxSizing: 'border-box'
    };
}

/**
 * Returns the recommended inner padding to give the product image breathing room inside the graphic border.
 * @param {Object} styleSettings - Active customizer settings object
 * @returns {string|null} CSS padding string, or null
 */
export function getGraphicBorderPadding(styleSettings = {}) {
    const presetId = styleSettings.shopImageGraphicBorder || styleSettings.productImageGraphicBorder || 'none';
    if (!presetId || presetId === 'none') return null;

    const customPad = styleSettings.shopImageGraphicBorderPadding ?? styleSettings.productImageGraphicBorderPadding;
    if (customPad !== undefined && customPad !== '') {
        return addUnit(customPad);
    }
    const preset = GRAPHIC_BORDER_PRESETS.find(p => p.id === presetId);
    return preset?.defaultPadding || '10px';
}
